import type { Car } from "@/components/CarCard";
import type { BadgeCfg } from "./badge-types";

/* ───────────────────────── Типы бэкенда ───────────────────────── */

export type ApiCity = {
  slug: string;
  station: string;
  name: string;
  name_en?: string;
  is_active?: boolean;
  sort?: number;
  currency?: string; // символ валюты города (₽, ฿…); по умолчанию ₽
  image_url?: string; // картинка города (раздел «Сайт Автопилот») — для карусели городов
  badgeHero?: BadgeCfg; // бейдж над формой поиска (из settings.badge_hero)
  badgeCar?: BadgeCfg;  // бейдж на фото машин (из settings.badge_car)
};

export type ApiCar = {
  guid: string;
  name: string;
  carClass?: string;
  rentPrice?: number; // копейки, базовая суточная цена
  priceDiscount?: number; // копейки, цена со скидкой (0 — нет скидки)
  cost?: number; // копейки, ИТОГО за весь период из 1С (учитывает дробные сутки); 0 если не из поиска
  photos?: string[] | null;
  typeOfTransmission?: string;
  fuelType?: string;
  batteryCapacity?: number; // кВт·ч из 1С; > 0 = гибрид (PHEV)
  isHybrid?: boolean;
  seatCount?: number;
  numberOfBags?: number;
  engineVolume?: string;
  unit?: string; // привод (Передний/Задний/Полный)
  yearOfManufacture?: number;
  features?: { guid?: string; name: string; name_en?: string; sort?: number }[]; // характеристики машины (из 1С)
  // Поля для страницы машины (приходят в /fleet, но не нужны в карточке списка):
  fuelConsumption?: string; // л/100км
  carColor?: string;
  deposit?: number; // копейки, залог
  minAge?: number;
  minDrivingExperience?: number;
  officeId?: string;   // GUID офиса — служебное
  officeName?: string; // имя офиса (филиала) из 1С (теперь и в GetCars, и в поиске)
  officeNameEn?: string; // AI-перевод имени филиала (для /en)
  filial?: string;     // имя филиала от бэкенда — НЕ пусто ТОЛЬКО у мультифилиальных городов
};

const FALLBACK_IMAGE = "/images/catalog-car.png";

/* ───────────────────────── Запросы ───────────────────────── */
// Все пути относительные — next.config проксирует /api/v1/* в gateway.

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchCities(): Promise<ApiCity[]> {
  // Источник — rent-site/cities: уже отфильтрован/отсортирован и несёт валюту
  // города (для цен в карточках).
  const data = await getJSON<{ cities?: (ApiCity & { settings?: { badge_hero?: BadgeCfg; badge_car?: BadgeCfg } })[] }>("/api/v1/rent-site/cities");
  return (data.cities || []).map((c) => ({
    ...c,
    badgeHero: c.settings?.badge_hero,
    badgeCar: c.settings?.badge_car,
  }));
}

// Все названия машин станции (вкл. неактивные) — чтобы фильтр показывал все
// марки/модели, даже если сейчас нет активной машины этой модели.
export async function fetchCatalogNames(station: string): Promise<string[]> {
  if (!station) return [];
  try {
    const d = await getJSON<{ names?: string[] }>(`/api/v1/catalog/models/${encodeURIComponent(station)}`);
    return d.names || [];
  } catch {
    return [];
  }
}

export async function fetchFleet(station: string): Promise<ApiCar[]> {
  const data = await getJSON<{ cars?: ApiCar[] }>(`/api/v1/fleet/${encodeURIComponent(station)}?source=autopilot`);
  return data.cars || [];
}

export type SearchParams = {
  station: string;
  startDate: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endDate: string;
  endTime: string;
  deliveryGuid?: string; // выбранное место подачи → бэкенд оставит только машины его филиала
};

export async function searchCars(p: SearchParams): Promise<ApiCar[]> {
  const q = new URLSearchParams({
    station: p.station,
    start_date: p.startDate,
    start_time: p.startTime,
    end_date: p.endDate,
    end_time: p.endTime,
    source: "autopilot", // брать только пак Автопилота (не Солнышко), иначе сток
  });
  // Место подачи выбрано → ограничиваем выдачу его филиалом (машины без офиса
  // остаются). Иначе (машина в Адлере) в аренду попадали бы места/машины Сочи.
  if (p.deliveryGuid) q.set("delivery_guid", p.deliveryGuid);
  const data = await getJSON<{ cars?: ApiCar[] }>(`/api/v1/search?${q.toString()}`);
  return data.cars || [];
}

/* ───────────────────────── Маппинг в карточку ───────────────────────── */

function fmtPrice(kopecks: number, currency = "₽"): string {
  return `${Math.round(kopecks / 100).toLocaleString("ru-RU")} ${currency}`;
}

/** Нормализует имя филиала из 1С: «Основной офис» — шум (так у большинства
 *  городов), его не показываем; пустое имя → undefined (бейдж без филиала). */
function cleanFilial(name?: string): string | undefined {
  const v = name?.trim();
  if (!v || v.toLowerCase() === "основной офис") return undefined;
  return v;
}

/** Преобразует машину бэкенда в пропсы CarCard. mode влияет на подпись к цене.
 *  currency — символ валюты города (₽ по умолчанию, ฿ для Пхукета и т.п.). */
export function mapCar(c: ApiCar, mode: "fleet" | "search", days?: number, currency = "₽"): Car {
  const base = c.rentPrice ?? 0;
  const discounted = c.priceDiscount && c.priceDiscount > 0 ? c.priceDiscount : 0;
  // Эффективная суточная = priceDiscount (это Cost/Period). Может быть НИЖЕ базы
  // (скидка за длинный срок) или ВЫШЕ (надбавка за короткий/дробный, напр. 2.5 сут).
  // Показываем всегда её; перечёркнутую базу и бейдж «скидка» — только при disc<base.
  const eff = discounted > 0 ? discounted : base;
  const hasDiscount = discounted > 0 && discounted < base;
  const current = eff;
  // Итого за период из 1С (учитывает дробные сутки). Фолбэк — сутки×дни.
  const costK = c.cost && c.cost > 0 ? c.cost : 0;

  let total: string | undefined;
  if (mode === "search" && days && days > 0) {
    // Период из 1С (Cost/эфф.суточная) — дробный (напр. 2.5); фолбэк — целые дни.
    const per = costK > 0 && current > 0 ? Math.round((costK / current) * 10) / 10 : days;
    total = `${fmtPrice(costK || current * days, currency)} за ${per} дн.`;
  } else {
    total = "в сутки";
  }

  return {
    id: c.guid,
    title: c.name,
    image: c.photos?.[0] || FALLBACK_IMAGE,
    rating: { value: 5.0, count: 0 }, // рейтингов в 1С нет — нейтральный плейсхолдер
    badges: hasDiscount ? ["sale"] : [],
    features: {
      transmission: c.typeOfTransmission || "—",
      drive: c.engineVolume ? `${c.engineVolume} л` : "—",
      // Батарея > 0 → машина PHEV: в ТТХ и фильтре топлива показываем «Гибрид».
      fuel: (c.isHybrid || (c.batteryCapacity || 0) > 0) ? "Гибрид" : (c.fuelType || "—"),
      seats: c.seatCount ? `${c.seatCount} мест` : "—",
    },
    price: {
      current: fmtPrice(current, currency),
      old: hasDiscount ? fmtPrice(base, currency) : undefined,
      total,
    },
    carClass: c.carClass || undefined,
    priceKopecks: current,
    costKopecks: costK,
    seatsNum: c.seatCount || 0,
    bagsNum: c.numberOfBags || 0,
    driveUnit: c.unit || undefined,
    currencySym: currency,
    carFeatures: (c.features || []).map((f) => ({ name: f.name, name_en: f.name_en, sort: f.sort })),
    year: c.yearOfManufacture,
    color: c.carColor,
    // Филиал — из бэкенда (не пусто ТОЛЬКО у мультифилиальных городов: Сочи/Адлер и т.п.).
    // Фолбэк на cleanFilial — пока бэкенд без поля (старый деплой).
    filial: c.filial !== undefined ? (c.filial || undefined) : cleanFilial(c.officeName),
    filialEn: c.filial !== undefined ? (c.filial ? cleanFilial(c.officeNameEn) : undefined) : cleanFilial(c.officeNameEn),
    isHybrid: !!c.isHybrid || (c.batteryCapacity || 0) > 0,
  };
}

/* ───────────── Бронирование (логика как у Солнышко, тот же gateway) ───────────── */

export type DeliveryPlace = {
  guid: string;
  name: string;
  name_en?: string;
  price?: number; // в валюте города
  dont_see_on_site?: boolean;
  sort?: number; // порядок в списке (из админки); первый = по умолчанию
};
export type DeliveryData = { cities: DeliveryPlace[]; regions: DeliveryPlace[] };

// Порядок мест подачи: 0 = «без приоритета» → в конец; первое = по умолчанию.
// Тот же закон, что в админке и на сайтах Солнышко.
function byDeliverySort(a: DeliveryPlace, b: DeliveryPlace) {
  const sa = a.sort || 0, sb = b.sort || 0;
  if (sa === 0 && sb !== 0) return 1;
  if (sb === 0 && sa !== 0) return -1;
  return sa - sb;
}

// ── Отзывы (Яндекс widget + 2ГИС API, парсятся в gateway) ──────────────────
export type ReviewItem = {
  source: "yandex" | "twogis" | string;
  station: string; office: string;
  author: string; avatar: string; rating: number; text: string; date: string; ts: number;
};
export type ReviewSourceLink = {
  source: string; station: string; office: string; url: string; rating: number; count: number;
};
// station пустой → отзывы по всем городам (главная); иначе только по городу.
export async function fetchReviews(station?: string): Promise<{ reviews: ReviewItem[]; sources: ReviewSourceLink[] }> {
  try {
    const q = station ? `?station=${encodeURIComponent(station)}` : "";
    const d = await getJSON<{ reviews?: ReviewItem[]; sources?: ReviewSourceLink[] }>(`/api/v1/reviews${q}`);
    return { reviews: d.reviews || [], sources: d.sources || [] };
  } catch {
    return { reviews: [], sources: [] };
  }
}

// Места подачи/возврата и регионы выезда города (для формы дат/места и брони).
// office — GUID филиала машины: показываем только места выдачи её филиала.
export async function fetchDeliveryPlaces(station: string, office?: string): Promise<DeliveryData> {
  try {
    const q = office ? `?office=${encodeURIComponent(office)}` : "";
    const d = await getJSON<{ cities?: DeliveryPlace[]; regions?: DeliveryPlace[] }>(
      `/api/v1/delivery-places/${encodeURIComponent(station)}${q}`,
    );
    return {
      cities: (d.cities || []).filter((c) => !c.dont_see_on_site).slice().sort(byDeliverySort),
      regions: (d.regions || []).filter((r) => !r.dont_see_on_site),
    };
  } catch {
    return { cities: [], regions: [] };
  }
}

// Опция/услуга/оборудование/регион машины (GET /cars/{station}/{carId}/options).
// type: 1=регион выезда, 2=услуга/страховка, 3=оборудование.
export type OptionItem = {
  guid: string;
  name: string;
  name_en?: string;
  price: number;       // в валюте города (за сутки если is_day, иначе разово)
  is_day?: boolean;
  type: number;
  is_active?: boolean;
  show_on_booking?: boolean;
  max_billable_days?: number;
  category_name?: string;
  description_ru?: string;
  description_en?: string;
  description_cn?: string;
  /** «Увеличивает цену при отказе»: если услуга НЕ выбрана, стоимость суток
   *  аренды растёт на price_increase_percentage%. Из 1С (IncreasesThePrice). */
  increases_price?: boolean;
  price_increase_percentage?: number;
};

// Черновик брони (GET /bookings/{uid}). Несёт station/city_slug (мультигород).
export type BookingDraft = {
  uid: string;
  phone?: string;
  car_id?: string;
  car_name?: string;
  station?: string;
  city_slug?: string;
  date_from?: string;
  date_to?: string;
  start_time?: string;
  end_time?: string;
  option_guid?: string;
  region_guid?: string;
  email?: string;
  state?: string | Record<string, unknown> | null;
};

// Сохранить состояние брони (опции/покрытия/цены/клиент) — между шагами.
export async function saveBookingState(uid: string, state: unknown): Promise<void> {
  const res = await fetch(`/api/v1/bookings/${encodeURIComponent(uid)}/state`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(state),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Лид «Аренда в 1 клик» — только телефон; на бэке уходит в TG-чат города
// (модель + город + телефон + хештег). station — код станции машины.
export async function oneClickLead(p: { station?: string; city?: string; car?: string; phone: string }): Promise<void> {
  const res = await fetch(`/api/v1/one-click`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Проверка промокода (скидка % на аренду). valid=false если не найден/истёк.
export async function checkPromo(station: string, code: string): Promise<{ valid: boolean; percent?: number; message?: string }> {
  try {
    const d = await getJSON<{ valid?: boolean; percent?: number; message?: string }>(
      `/api/v1/promo/check?station=${encodeURIComponent(station)}&code=${encodeURIComponent(code)}`,
    );
    return { valid: !!d.valid, percent: d.percent, message: d.message };
  } catch {
    return { valid: false, message: "Промокод не найден" };
  }
}

// Финализация брони → ссылка на оплату (Альфа) либо статус success (без оплаты).
export async function finalizeBooking(uid: string, phone: string): Promise<{ pay_url?: string; status?: string }> {
  const res = await fetch(`/api/v1/bookings/done`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uid, data: phone }),
  });
  if (!res.ok) {
    // Прокидываем код ошибки (напр. CAR_ALREADY_RESERVED — машина уже в резерве),
    // чтобы обработчик сабмита показал точный тост.
    const body = await res.json().catch(() => ({} as { code?: string; error?: string }));
    const err = new Error(body?.error || `HTTP ${res.status}`) as Error & { code?: string };
    err.code = body?.code || "";
    throw err;
  }
  return await res.json();
}

// Статус оплаты Альфа по брони (poll на странице успеха).
export async function getPaymentStatus(uid: string): Promise<{ status: string; amount?: number; pay_url?: string }> {
  try {
    const d = await getJSON<{ status?: string; amount?: number; pay_url?: string }>(`/api/v1/payment/alfa/status?uid=${encodeURIComponent(uid)}`);
    return { status: d.status || "", amount: d.amount, pay_url: d.pay_url };
  } catch {
    return { status: "" };
  }
}

export type CreateBookingInput = {
  phone: string;
  carId: string;
  carName: string;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;
  startTime: string; // HH:MM
  endTime: string;
  optionGuid?: string;
  regionGuid?: string;
  station: string;
  citySlug: string;
};

// Создаёт черновик брони, возвращает uid → переход на /booking?b={uid}.
export async function createBooking(i: CreateBookingInput): Promise<string> {
  const res = await fetch(`/api/v1/bookings/new`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      data: i.phone, cid: i.carId, cn: i.carName,
      df: i.dateFrom, dt: i.dateTo, st: i.startTime, et: i.endTime,
      og: i.optionGuid || "", rg: i.regionGuid || "",
      station: i.station, city_slug: i.citySlug,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const d = await res.json();
  return d.uid as string;
}
